import type { ObsInput } from "../../../components/ObservationChart";
import { type ObsMetricAlias, obsMetricNames } from "../../../resources/observations_metricUtils";
import { O2_SATURATION_LIMITS } from "@/components/ObservationChart";

// -- limits

export const startOfPageY = 105; // i.e. generally where we can start writing
export const endOfPageY = 815; // i.e. do not go past here in the document
export const defaultWidth = 495; // i.e. the writable width of the page
export const heightOfTableRow = 20;

// -- types

export interface PrintableObservationTableSection {
  dataKey: ObsMetricAlias;
  inputs: ObsInput[];
  pdfStartCoords: [number, number];
}

// -- functional config

export interface MevSummaryAutoOpenConfig {
  enabled: boolean;
  triggerFieldKeys: string[];
  allowResumeExpected: boolean;
  excludeConcierge: boolean;
}

export const mevSummaryAutoOpenConfig: MevSummaryAutoOpenConfig = {
  enabled: true,
  triggerFieldKeys: [
    // for saves beyond the inital creation, touches to one or more of the following fields will trigger the PDF to be opened (page/sec 1 only)
    "atsScore",
    // "firstName", // example: uncomment to trigger auto-open when firstName changes
  ],
  allowResumeExpected: true,
  excludeConcierge: true,
};

// -- apperance

// NB: all of the grid spacing is set for an even page. For odd pages, we provide a nudge
export const tlhsCellHeader = [128, 96.5] as [x: number, y: number];
export const tlhsCell = [128, 135] as [x: number, y: number];
export const cellVertDistance = 11.075;
export const cellHorizDistance = 20.4;
export const dotRadius = 3;
// dimensions of the obs chart grid (usable portion)
export const countCellsAcross = 19;
export const countCellsHeigh = 57;

/**
 * This should stay pretty close to the one being used in the portal in:
 * `apps/portal/src/components/ObservationChart/index.tsx`
 */
export const getPrintableObsChartDef = (hypercapnicReceivingOxygen: boolean): PrintableObservationTableSection[] => {
  const extraRowOffset = hypercapnicReceivingOxygen ? 3 : 0;
  // When consciousness is compressed from 5 rows (dots) to 1 row (text), we save 4 rows
  const consciousnessCompressionOffset = hypercapnicReceivingOxygen ? -4 : 0;

  return [
    // -- Respiratory Rate
    {
      dataKey: obsMetricNames.respirations,
      pdfStartCoords: tlhsCell,
      inputs: [
        { label: "≥ 36", range: { min: 36, max: 999 }, news: 3, tooHigh: true },
        { label: "30-35", range: { min: 30, max: 35.999 }, news: 2, tooHigh: true },
        { label: "25-29", range: { min: 25, max: 29.999 }, news: 1, tooHigh: true },
        { label: "21-24", range: { min: 21, max: 24.999 } },
        { label: "15-20", range: { min: 15, max: 20.999 } },
        { label: "10-14", range: { min: 10, max: 14.999 } },
        { label: "5-9", range: { min: 5, max: 9.999 }, news: 2 },
        { label: "≤ 4", range: { min: -999, max: 4.999 }, news: 3 },
      ],
    },
    // -- O2 Saturation
    {
      dataKey: obsMetricNames.o2Sats,
      pdfStartCoords: [
        tlhsCell[0],
        // the vertical position also changes if it's a hypercapnic patient receiving oxygen...we'll actually it's the next section after this one that changes...
        tlhsCell[1] + cellVertDistance * 8,
      ],
      // We'll dynamically set the inputs based on the patient's condition
      inputs: O2_SATURATION_LIMITS.default.ranges,
    },
    // -- O3 Flow
    {
      dataKey: obsMetricNames.o2Flow,
      pdfStartCoords: [
        tlhsCell[0],
        tlhsCell[1] + cellVertDistance * (12 + extraRowOffset),
      ],
      inputs: [
        { label: "≥ 10", range: { min: 10, max: 20 }, news: 3, tooHigh: true },
        { label: "7-9", range: { min: 7, max: 9.999 }, news: 2, tooHigh: true },
        { label: "3-6", range: { min: 3, max: 6.999 }, news: 1, tooHigh: true },
        { label: "≤ 2", range: { min: 0, max: 2.999 } },
      ],
    },
    // -- 04 Heart Rate
    {
      dataKey: obsMetricNames.heartRate,
      pdfStartCoords: [
        tlhsCell[0],
        tlhsCell[1] + cellVertDistance * (16 + extraRowOffset),
      ],
      inputs: [
        { label: "≥ 140", range: { min: 140, max: 200 }, news: 3, tooHigh: true },
        { label: "130s", range: { min: 130, max: 139.999 }, news: 2, tooHigh: true },
        { label: "120s", range: { min: 120, max: 129.999 }, news: 2, tooHigh: true },
        { label: "110s", range: { min: 110, max: 119.999 }, news: 1, tooHigh: true },
        { label: "100s", range: { min: 100, max: 109.999 } },
        { label: "90s", range: { min: 90, max: 99.999 } },
        { label: "80s", range: { min: 80, max: 89.999 } },
        { label: "70s", range: { min: 70, max: 79.999 } },
        { label: "60s", range: { min: 60, max: 69.999 } },
        { label: "50s", range: { min: 50, max: 59.999 }, news: 1 },
        { label: "40s", range: { min: 40, max: 49.999 }, news: 2 },
        { label: "≤ 30", range: { min: 0, max: 30 }, news: 3 },
      ],
    },
    // -- 05 Blood Pressure
    {
      dataKey: obsMetricNames.bloodPressure,
      pdfStartCoords: [
        tlhsCell[0],
        tlhsCell[1] + cellVertDistance * (28 + extraRowOffset),
      ],
      inputs: [
        { label: "≥ 200", range: { min: 200, max: 300 }, news: 3, tooHigh: true },
        { label: "190s", range: { min: 190, max: 199.999 }, news: 2, tooHigh: true },
        { label: "180s", range: { min: 180, max: 189.999 }, news: 2, tooHigh: true },
        { label: "170s", range: { min: 170, max: 179.999 }, news: 2, tooHigh: true },
        { label: "160s", range: { min: 160, max: 169.999 }, news: 1, tooHigh: true },
        { label: "150s", range: { min: 150, max: 159.999 } },
        { label: "140s", range: { min: 140, max: 149.999 } },
        { label: "130s", range: { min: 130, max: 139.999 } },
        { label: "120s", range: { min: 120, max: 129.999 } },
        { label: "110s", range: { min: 110, max: 119.999 } },
        { label: "100s", range: { min: 100, max: 109.999 }, news: 1 },
        { label: "90s", range: { min: 90, max: 99.999 }, news: 2 },
        { label: "80s", range: { min: 80, max: 89.999 }, news: 3 },
        { label: "70s", range: { min: 70, max: 79.999 }, news: 3 },
        { label: "60s", range: { min: 60, max: 69.999 }, news: 3 },
        { label: "50s", range: { min: 50, max: 59.999 }, news: 3 },
        { label: "40s", range: { min: 40, max: 49.999 }, news: 3 },
      ],
    },
    // -- 06 Temperature
    {
      dataKey: obsMetricNames.temperature,
      pdfStartCoords: [
        tlhsCell[0],
        tlhsCell[1] + cellVertDistance * (45 + extraRowOffset),
      ],
      inputs: [
        { label: "≥ 39", range: { min: 39, max: 999 }, news: 3, tooHigh: true },
        { label: "", range: { min: 38.5, max: 38.999 }, news: 2, tooHigh: true },
        { label: "", range: { min: 38, max: 38.4999 }, news: 1, tooHigh: true },
        { label: "", range: { min: 37.5, max: 37.999 } },
        { label: "", range: { min: 36.6, max: 37.4999 } },
        { label: "", range: { min: 35.1, max: 36.5999 } },
        { label: "≤ 35", range: { min: 0, max: 35.0999 }, news: 2 },
      ],
    },
    // -- 06 Consciousness
    {
      dataKey: obsMetricNames.consciousness,
      pdfStartCoords: [
        tlhsCell[0],
        tlhsCell[1] + cellVertDistance * ((hypercapnicReceivingOxygen ? 51 : 52) + extraRowOffset),
      ],
      inputs: [
        { label: "Alert", value: "A" },
        { label: "Confused", value: "C", news: 2 as const },
        { label: "Verbal", value: "V", news: 2 as const },
        { label: "Painful", value: "P", news: 3 as const },
        { label: "Unresp.", value: "U", news: 4 as const },
      ],
    },
    // -- 07 News2
    {
      dataKey: obsMetricNames.riskScore,
      pdfStartCoords: [
        tlhsCell[0],
        tlhsCell[1] + cellVertDistance * (56 + extraRowOffset + consciousnessCompressionOffset),
      ],
      inputs: [
        { showOriginValue: true, range: { min: 0, max: 300 } }
      ],
    },
    // -- 08 rythm
    {
      dataKey: obsMetricNames.rhythm,
      pdfStartCoords: [
        tlhsCell[0],
        tlhsCell[1] + cellVertDistance * (57 + extraRowOffset + consciousnessCompressionOffset),
      ],
      inputs: [
        { showOriginValue: true, range: { min: 0, max: 300 } }
      ],
    },
    // -- 09 BGL
    {
      dataKey: obsMetricNames.bloodGlucose,
      pdfStartCoords: [
        tlhsCell[0],
        tlhsCell[1] + cellVertDistance * (58 + extraRowOffset + consciousnessCompressionOffset),
      ],
      inputs: [
        { showOriginValue: true, range: { min: 0, max: 100 } }
      ],
    },
    // -- 10 Blood Alcohol
    {
      dataKey: obsMetricNames.bloodAlcohol,
      pdfStartCoords: [
        tlhsCell[0],
        tlhsCell[1] + cellVertDistance * (59 + extraRowOffset + consciousnessCompressionOffset),
      ],
      inputs: [
        { showOriginValue: true, range: { min: 0, max: 1 } }
      ],
    },
    // -- 11 Pain Score
    {
      dataKey: obsMetricNames.painScore,
      pdfStartCoords: [
        tlhsCell[0],
        tlhsCell[1] + cellVertDistance * (60 + extraRowOffset + consciousnessCompressionOffset),
      ],
      inputs: [
        { showOriginValue: true, range: { min: 0, max: 10 } }
      ],
    },
  ];
};

export const cellHorizDistanceNeuro = 36.8;
export const cellVertDistanceNeuro = 13.75;
export const headerCellVertDistanceNeuro = cellVertDistanceNeuro + 0.2;
export const initalsNtlhsCell = [172, 120] as [x: number, y: number];
export const ntlhsCell = [158, 180] as [x: number, y: number];
// neuro limb table with red header
export const heightLimbNeuroRow = 18;
export const neuroLimbHorizDist = 46.9;
export const neuroLeftArmStartX = 82;
export const neuroLeftArmStartY = 687;
const neuroRightArmStartX = 113;
const neuroRightArmStartY = neuroLeftArmStartY;
const neuroLegNudgeY = 8.5;


export const printableNeuroObsChartDef: PrintableObservationTableSection[] = [
  // -- GCS - Eye Opening
  {
    dataKey: obsMetricNames.gcsEyes,
    pdfStartCoords: [
      ntlhsCell[0],
      ntlhsCell[1],
    ],
    inputs: [
      { label: "4 Spontaneous", value: "4", range: { min: 4, max: 4 } },
      { label: "3 To sound", value: "3", range: { min: 3, max: 3 } },
      { label: "2 To pressure", value: "2", range: { min: 2, max: 2 } },
      { label: "1 None", value: "1", range: { min: 1, max: 1 } },
      { label: "C", value: "C", range: { min: 0, max: 0 } },
    ],
  },
  // -- GCS - Verbal Response
  {
    dataKey: obsMetricNames.gcsVerbal,
    pdfStartCoords: [
      ntlhsCell[0],
      ntlhsCell[1] + (cellVertDistanceNeuro * 4) + headerCellVertDistanceNeuro,
    ],
    inputs: [
      { label: "5 Oriented", value: "5", range: { min: 5, max: 5 } },
      { label: "4 Confused", value: "4", range: { min: 4, max: 4 } },
      { label: "3 Inappropriate", value: "3", range: { min: 3, max: 3 } },
      { label: "2 Incomprehensible", value: "2", range: { min: 2, max: 2 } },
      { label: "1 None", value: "1", range: { min: 1, max: 1 } },
      { label: "T", value: "T", range: { min: 0, max: 0 } },
    ],
  },
  // -- GCS - Motor Response
  {
    dataKey: obsMetricNames.gcsMovement,
    pdfStartCoords: [
      ntlhsCell[0],
      ntlhsCell[1] + (cellVertDistanceNeuro * 9) + (headerCellVertDistanceNeuro * 2) + 2,
    ],
    inputs: [
      { label: "6 Obey commands", value: "6", range: { min: 6, max: 6 } },
      { label: "5 Localise to pressure", value: "5", range: { min: 5, max: 5 } },
      { label: "4 Normal flexion", value: "4", range: { min: 4, max: 4 } },
      { label: "3 Abnormal flexion", value: "3", range: { min: 3, max: 3 } },
      { label: "2 Extension", value: "2", range: { min: 2, max: 2 } },
      { label: "1 None", value: "1", range: { min: 1, max: 1 } },
    ],
  },
  // -- GCS - Total
  {
    dataKey: obsMetricNames.gcsTotal,
    pdfStartCoords: [
      ntlhsCell[0] + 13.8,
      ntlhsCell[1] + (cellVertDistanceNeuro * 14.35) + (headerCellVertDistanceNeuro * 2) + 2,
    ],
    inputs: [
      { showOriginValue: true, range: { min: 0, max: 15 } }
    ],
  },
  // -- Pupil Right - Size
  {
    dataKey: obsMetricNames.pupilRightSize,
    pdfStartCoords: [
      ntlhsCell[0] + 13.8,
      ntlhsCell[1] + (cellVertDistanceNeuro * 17.5) + (headerCellVertDistanceNeuro * 2),
    ],
    inputs: [
      { showOriginValue: true, range: { min: 0, max: 10 } }
    ],
  },
  // -- Pupil Right - Reaction
  {
    dataKey: obsMetricNames.pupilRightReaction,
    pdfStartCoords: [
      ntlhsCell[0] + 13.8,
      ntlhsCell[1] + (cellVertDistanceNeuro * 18.5) + (headerCellVertDistanceNeuro * 2),
    ],
    inputs: [
      { showOriginValue: true, range: { min: 0, max: 999 } }
    ],
  },
  // -- Pupil Left - Size
  {
    dataKey: obsMetricNames.pupilLeftSize,
    pdfStartCoords: [
      ntlhsCell[0] + 13.8,
      ntlhsCell[1] + (cellVertDistanceNeuro * 19.5) + (headerCellVertDistanceNeuro * 2),
    ],
    inputs: [
      { showOriginValue: true, range: { min: 0, max: 10 } }
    ],
  },
  // -- Pupil Left - Reaction
  {
    dataKey: obsMetricNames.pupilLeftReaction,
    pdfStartCoords: [
      ntlhsCell[0] + 13.8,
      ntlhsCell[1] + (cellVertDistanceNeuro * 20.5) + (headerCellVertDistanceNeuro * 2),
    ],
    inputs: [
      { showOriginValue: true, range: { min: 0, max: 999 } }
    ],
  },
  // -- Limb Movement (ARMS) - Right Arm
  {
    dataKey: obsMetricNames.limbMovRightArm,
    pdfStartCoords: [
      ntlhsCell[0],
      ntlhsCell[1] + (cellVertDistanceNeuro * 22.2) + (headerCellVertDistanceNeuro * 2),
    ],
    inputs: [
      { label: "normal", value: "normal" },
      { label: "mild", value: "mild" },
      { label: "severe", value: "severe" },
      { label: "spastic", value: "spastic" },
      { label: "extension", value: "extension" },
      { label: "none", value: "none" },
    ],
  },
  // -- Limb Movement (ARMS) - Left Arm
  {
    dataKey: obsMetricNames.limbMovLeftArm,
    pdfStartCoords: [
      ntlhsCell[0] + 12.95, // +1 sub-col to the right
      ntlhsCell[1] + (cellVertDistanceNeuro * 22.2) + (headerCellVertDistanceNeuro * 2),
    ],
    inputs: [
      { label: "normal", value: "normal" },
      { label: "mild", value: "mild" },
      { label: "severe", value: "severe" },
      { label: "spastic", value: "spastic" },
      { label: "extension", value: "extension" },
      { label: "none", value: "none" },
    ],
  },
  // -- Limb Movement (LEGS) - Right Leg
  {
    dataKey: obsMetricNames.limbMovRightLeg,
    pdfStartCoords: [
      ntlhsCell[0],
      ntlhsCell[1] + (cellVertDistanceNeuro * 28.2) + (headerCellVertDistanceNeuro * 2),
    ],
    inputs: [
      { label: "normal", value: "normal" },
      { label: "mild", value: "mild" },
      { label: "severe", value: "severe" },
      // { label: "spastic", value: "spastic" }, // not on legs...
      { label: "extension", value: "extension" },
      { label: "none", value: "none" },
    ],
  },
  // -- Limb Movement (LEGS) - Left Leg
  {
    dataKey: obsMetricNames.limbMovLeftLeg,
    pdfStartCoords: [
      ntlhsCell[0] + 12.95, // +1 sub-col to the right
      ntlhsCell[1] + (cellVertDistanceNeuro * 28.2) + (headerCellVertDistanceNeuro * 2),
    ],
    inputs: [
      { label: "normal", value: "normal" },
      { label: "mild", value: "mild" },
      { label: "severe", value: "severe" },
      // { label: "spastic", value: "spastic" }, // not on legs...
      { label: "extension", value: "extension" },
      { label: "none", value: "none" },
    ],
  },
  // -- Limb Neurovascular (ARMS) - Right Arm
  {
    dataKey: obsMetricNames.limbNeuroRightArmColour,
    pdfStartCoords: [
      neuroRightArmStartX,
      neuroRightArmStartY,
    ],
    inputs: [
      { showOriginValue: true, range: { min: 0, max: 10 } }
    ],
  },
  {
    dataKey: obsMetricNames.limbNeuroRightArmWarmth,
    pdfStartCoords: [
      neuroRightArmStartX + neuroLimbHorizDist,
      neuroRightArmStartY,
    ],
    inputs: [
      { showOriginValue: true, range: { min: 0, max: 10 } }
    ],
  },
  {
    dataKey: obsMetricNames.limbNeuroRightArmMovement,
    pdfStartCoords: [
      neuroRightArmStartX + (neuroLimbHorizDist * 2),
      neuroRightArmStartY,
    ],
    inputs: [
      { showOriginValue: true, range: { min: 0, max: 10 } }
    ],
  },
  {
    dataKey: obsMetricNames.limbNeuroRightArmSensation,
    pdfStartCoords: [
      neuroRightArmStartX + (neuroLimbHorizDist * 3),
      neuroRightArmStartY,
    ],
    inputs: [
      { showOriginValue: true, range: { min: 0, max: 10 } }
    ],
  },
  {
    dataKey: obsMetricNames.limbNeuroRightArmCapillaryRefill,
    pdfStartCoords: [
      neuroRightArmStartX + (neuroLimbHorizDist * 4),
      neuroRightArmStartY,
    ],
    inputs: [
      { showOriginValue: true, range: { min: 0, max: 10 } }
    ],
  },
  {
    dataKey: obsMetricNames.limbNeuroRightArmPulse,
    pdfStartCoords: [
      neuroRightArmStartX + (neuroLimbHorizDist * 5),
      neuroRightArmStartY,
    ],
    inputs: [
      { showOriginValue: true, range: { min: 0, max: 10 } }
    ],
  },
  // -- Limb Neurovascular (ARMS) - Left Arm
  {
    dataKey: obsMetricNames.limbNeuroLeftArmColour,
    pdfStartCoords: [
      neuroLeftArmStartX,
      neuroLeftArmStartY,
    ],
    inputs: [
      { showOriginValue: true, range: { min: 0, max: 10 } }
    ],
  },
  {
    dataKey: obsMetricNames.limbNeuroLeftArmWarmth,
    pdfStartCoords: [
      neuroLeftArmStartX + neuroLimbHorizDist,
      neuroLeftArmStartY,
    ],
    inputs: [
      { showOriginValue: true, range: { min: 0, max: 10 } }
    ],
  },
  {
    dataKey: obsMetricNames.limbNeuroLeftArmMovement,
    pdfStartCoords: [
      neuroLeftArmStartX + (neuroLimbHorizDist * 2),
      neuroLeftArmStartY,
    ],
    inputs: [
      { showOriginValue: true, range: { min: 0, max: 10 } }
    ],
  },
  {
    dataKey: obsMetricNames.limbNeuroLeftArmSensation,
    pdfStartCoords: [
      neuroLeftArmStartX + (neuroLimbHorizDist * 3),
      neuroLeftArmStartY,
    ],
    inputs: [
      { showOriginValue: true, range: { min: 0, max: 10 } }
    ],
  },
  {
    dataKey: obsMetricNames.limbNeuroLeftArmCapillaryRefill,
    pdfStartCoords: [
      neuroLeftArmStartX + (neuroLimbHorizDist * 4),
      neuroLeftArmStartY,
    ],
    inputs: [
      { showOriginValue: true, range: { min: 0, max: 10 } }
    ],
  },
  {
    dataKey: obsMetricNames.limbNeuroLeftArmPulse,
    pdfStartCoords: [
      neuroLeftArmStartX + (neuroLimbHorizDist * 5),
      neuroLeftArmStartY,
    ],
    inputs: [
      { showOriginValue: true, range: { min: 0, max: 10 } }
    ],
  },
  // -- Limb Neurovascular (LEGS) - Right Leg
  {
    dataKey: obsMetricNames.limbNeuroRightLegColour,
    pdfStartCoords: [
      neuroRightArmStartX,
      neuroRightArmStartY + neuroLegNudgeY,
    ],
    inputs: [
      { showOriginValue: true, range: { min: 0, max: 10 } }
    ],
  },
  {
    dataKey: obsMetricNames.limbNeuroRightLegWarmth,
    pdfStartCoords: [
      neuroRightArmStartX + neuroLimbHorizDist,
      neuroRightArmStartY + neuroLegNudgeY,
    ],
    inputs: [
      { showOriginValue: true, range: { min: 0, max: 10 } }
    ],
  },
  {
    dataKey: obsMetricNames.limbNeuroRightLegMovement,
    pdfStartCoords: [
      neuroRightArmStartX + (neuroLimbHorizDist * 2),
      neuroRightArmStartY + neuroLegNudgeY,
    ],
    inputs: [
      { showOriginValue: true, range: { min: 0, max: 10 } }
    ],
  },
  {
    dataKey: obsMetricNames.limbNeuroRightLegSensation,
    pdfStartCoords: [
      neuroRightArmStartX + (neuroLimbHorizDist * 3),
      neuroRightArmStartY + neuroLegNudgeY,
    ],
    inputs: [
      { showOriginValue: true, range: { min: 0, max: 10 } }
    ],
  },
  {
    dataKey: obsMetricNames.limbNeuroRightLegCapillaryRefill,
    pdfStartCoords: [
      neuroRightArmStartX + (neuroLimbHorizDist * 4),
      neuroRightArmStartY + neuroLegNudgeY,
    ],
    inputs: [
      { showOriginValue: true, range: { min: 0, max: 10 } }
    ],
  },
  {
    dataKey: obsMetricNames.limbNeuroRightLegPulse,
    pdfStartCoords: [
      neuroRightArmStartX + (neuroLimbHorizDist * 5),
      neuroRightArmStartY + neuroLegNudgeY,
    ],
    inputs: [
      { showOriginValue: true, range: { min: 0, max: 10 } }
    ],
  },
  // -- Limb Neurovascular (LEGS) - Left Leg
  {
    dataKey: obsMetricNames.limbNeuroLeftLegColour,
    pdfStartCoords: [
      neuroLeftArmStartX,
      neuroLeftArmStartY + neuroLegNudgeY,
    ],
    inputs: [
      { showOriginValue: true, range: { min: 0, max: 10 } }
    ],
  },
  {
    dataKey: obsMetricNames.limbNeuroLeftLegWarmth,
    pdfStartCoords: [
      neuroLeftArmStartX + neuroLimbHorizDist,
      neuroLeftArmStartY + neuroLegNudgeY,
    ],
    inputs: [
      { showOriginValue: true, range: { min: 0, max: 10 } }
    ],
  },
  {
    dataKey: obsMetricNames.limbNeuroLeftLegMovement,
    pdfStartCoords: [
      neuroLeftArmStartX + (neuroLimbHorizDist * 2),
      neuroLeftArmStartY + neuroLegNudgeY,
    ],
    inputs: [
      { showOriginValue: true, range: { min: 0, max: 10 } }
    ],
  },
  {
    dataKey: obsMetricNames.limbNeuroLeftLegSensation,
    pdfStartCoords: [
      neuroLeftArmStartX + (neuroLimbHorizDist * 3),
      neuroLeftArmStartY + neuroLegNudgeY,
    ],
    inputs: [
      { showOriginValue: true, range: { min: 0, max: 10 } }
    ],
  },
  {
    dataKey: obsMetricNames.limbNeuroLeftLegCapillaryRefill,
    pdfStartCoords: [
      neuroLeftArmStartX + (neuroLimbHorizDist * 4),
      neuroLeftArmStartY + neuroLegNudgeY,
    ],
    inputs: [
      { showOriginValue: true, range: { min: 0, max: 10 } }
    ],
  },
  {
    dataKey: obsMetricNames.limbNeuroLeftLegPulse,
    pdfStartCoords: [
      neuroLeftArmStartX + (neuroLimbHorizDist * 5),
      neuroLeftArmStartY + neuroLegNudgeY,
    ],
    inputs: [
      { showOriginValue: true, range: { min: 0, max: 10 } }
    ],
  },
];
